/**
 * Static dummy data for the Reeve app — one coherent world shared by every
 * page, so the demo hangs together: law changes point at real documents,
 * cases reference the same people, and the numbers add up.
 *
 * The "today" of this world is mid-July 2026: the April 2026 measures of the
 * Employment Rights Act 2025 are in force, the January 2027 ones are coming.
 * All dates are pre-formatted strings — nothing here reads the live clock.
 */

import type {
  ActivityItem,
  Company,
  CompanyAssertion,
  CompanyDocument,
  Conversation,
  DocCategory,
  DocTemplate,
  Expert,
  HrCase,
  LawChange,
} from './types';

/* ------------------------------------------------------------ Categories */

/** Display metadata for the six document categories, in checklist order. */
export const CATEGORY_META: Record<DocCategory, { label: string; blurb: string; order: number }> = {
  'legally-required': {
    label: 'Legally required',
    blurb: 'The law says a business like yours must have these.',
    order: 0,
  },
  essential: {
    label: 'Essential',
    blurb: 'Not strictly statutory, but tribunals and disputes expect them. Going without is expensive.',
    order: 1,
  },
  'best-practice': {
    label: 'Best practice',
    blurb: 'Make the team easier and safer to run, and head off problems early.',
    order: 2,
  },
  optional: {
    label: 'Optional',
    blurb: 'Nice to have, and worth adding as you grow.',
    order: 3,
  },
  'not-relevant': {
    label: 'Not relevant to you',
    blurb: 'Fine to skip for a business like yours, and reeve will say if that changes.',
    order: 4,
  },
  'should-not-have': {
    label: 'Should not have',
    blurb: 'Unlawful, unenforceable or misleading: fix or remove these.',
    order: 5,
  },
};

export const CATEGORY_ORDER: DocCategory[] = (Object.keys(CATEGORY_META) as DocCategory[]).sort(
  (a, b) => CATEGORY_META[a].order - CATEGORY_META[b].order,
);

/**
 * What each tier actually MEANS, for the explainer box on a document page.
 *
 * Generic by design: this explains the label, not the document. "Essential" is doing a lot of work on
 * these pages and nobody arrives knowing that it means "no statute names it, but a tribunal will hold
 * it against you" — so the terms have to be able to explain themselves, in the one place a user is
 * already looking at one.
 *
 * The blurbs in CATEGORY_META are section headings, a line each. These are the long form.
 */
export const CATEGORY_EXPLAINER: Record<DocCategory, { title: string; body: string }> = {
  'legally-required': {
    title: 'Why is this legally required?',
    body: 'A specific law says a business like yours must have this. Not having it is a breach in itself, whether or not anything ever goes wrong and whether or not anyone complains. Depending on the rule that can mean a fine, an enforcement notice, or a finding made against you automatically if the matter ever reaches a tribunal. This is the tier to clear first.',
  },
  essential: {
    title: 'Why is this essential?',
    body: 'No single law names this document, so nobody will fine you for its absence. Going without it is still expensive. Tribunals, insurers and investigators expect a business your size to have one, and not having it is often what turns an arguable case into a lost one. The ACAS Code works exactly this way: ignoring it is not an offence, but it can add up to 25% to whatever you are ordered to pay. Treat these as required in practice.',
  },
  'best-practice': {
    title: 'Why is this best practice?',
    body: 'Nothing forces you to have this and there is no penalty for going without. It makes the business easier and safer to run, settles disagreements before they grow, and gives you something to point at when someone asks how you handle a situation. Worth doing once the tiers above are clear.',
  },
  optional: {
    title: 'Why is this optional?',
    body: 'A genuine nice-to-have. It solves a real problem, but only once you are big enough or complex enough to have that problem. No penalty, no expectation, no urgency. Come back to these when everything else is done.',
  },
  'not-relevant': {
    title: 'Why is this not relevant to you?',
    body: 'Nothing about how you have described your business brings this into scope, so you do not need it. It stays on the list so you can see it was considered rather than missed. If your answers change, or the law does, reeve moves it back onto your requirements and tells you why.',
  },
  'should-not-have': {
    title: 'Why should you not have this?',
    body: 'Holding this is worse than not holding it. The document is unlawful, unenforceable, or misleading about what the law actually allows, so relying on it can leave you worse off than having nothing written down at all. It can also be read as evidence of how you intended to treat people. Remove it, or replace it with a version that reflects current law.',
  },
};

/* --------------------------------------------------------------- Account */

/** The person who owns this portfolio account — used for greetings. */
export const ACCOUNT_OWNER_NAME = 'Bram';

/* ------------------------------------------------------------- Companies */

// compliance/openActions must agree with docStats() over the company's
// document list below — the dashboard computes live, the portfolio reads these.
export const COMPANIES: Company[] = [
  {
    id: 'bloom-forage',
    name: 'Bloom & Forage Ltd',
    sector: 'Hospitality & food service',
    location: 'Bristol',
    employees: 6,
    compliance: 71,
    openActions: 5,
    plan: 'Business',
  },
  {
    id: 'fernwell-studio',
    name: 'Fernwell Studio Ltd',
    sector: 'Design & creative services',
    location: 'Bath',
    employees: 3,
    compliance: 88,
    openActions: 1,
    plan: 'Business',
  },
  {
    id: 'hartley-roasters',
    name: 'Hartley Coffee Roasters Ltd',
    sector: 'Food production & retail',
    location: 'Bristol',
    employees: 11,
    compliance: 50,
    openActions: 6,
    plan: 'Business',
  },
];

export const DEFAULT_COMPANY_ID = 'bloom-forage';

export function getCompany(id: string): Company {
  return COMPANIES.find((c) => c.id === id) ?? COMPANIES[0];
}

/* ------------------------------------------------------------- Documents */

const BLOOM_DOCUMENTS: CompanyDocument[] = [
  {
    id: 'employment-contract',
    name: 'Employment contract',
    category: 'legally-required',
    status: 'ok',
    summary:
      'Every employee must get a written statement of particulars on or before day one. Yours doubles as the full contract.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'bloom-forage-contract-v4.docx',
    findings: [
      { severity: 'ok', text: 'Covers all the required particulars, including hours, pay and place of work.' },
      { severity: 'ok', text: 'Notice periods meet the statutory minimum for every length of service.' },
      {
        severity: 'warn',
        text: 'The probation clause leans on the two-year unfair dismissal window. From January 2027 protection starts at six months.',
        fix: 'No change needed yet. Reeve will flag the exact wording to change nearer the time.',
      },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['unfair-dismissal-6mo'],
    history: [
      { date: '3 Feb 2026', event: 'Uploaded v4 with new pay rates and break wording', by: 'you' },
      { date: '6 Apr 2026', event: 'Re-checked after the April Employment Rights Act changes: still compliant', by: 'reeve' },
      { date: '12 Sep 2025', event: 'Uploaded v3', by: 'you' },
    ],
    excerpts: [
      { text: '4.1 Your normal hours of work are 40 per week, worked over five days between Monday and Sunday, with rotas issued at least two weeks in advance.' },
      { text: '9.2 After successful completion of probation, either party may end this agreement with one month’s written notice, rising to two months after five years’ continuous service.' },
      { text: '11.4 You are entitled to 28 days’ paid holiday per year (including bank holidays), accruing monthly from your start date.' },
    ],
  },
  {
    id: 'h-and-s-policy',
    name: 'Health & safety policy',
    category: 'legally-required',
    status: 'ok',
    summary:
      'With five or more employees the law requires a written health & safety policy. Kitchens raise the bar: food hygiene, knives, hot equipment.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'hands-policy-2026.pdf',
    findings: [
      { severity: 'ok', text: 'Written policy in place and signed by the responsible director.' },
      { severity: 'ok', text: 'Covers kitchen-specific risks including burns, slips and manual handling.' },
    ],
    templateAvailable: true,
    history: [
      { date: '3 Feb 2026', event: 'Uploaded 2026 edition, signed', by: 'you' },
      { date: '4 Feb 2026', event: 'Assessed: no issues found', by: 'reeve' },
    ],
  },
  {
    id: 'privacy-notice',
    name: 'Staff privacy notice (UK GDPR)',
    category: 'legally-required',
    status: 'ok',
    summary:
      'Tells your team what personal data you hold about them and why. Required under UK GDPR from the moment you employ anyone.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 12 Mar 2026',
    fileName: 'staff-privacy-notice.pdf',
    findings: [
      { severity: 'ok', text: 'Covers the data you actually hold, including right-to-work copies and payroll.' },
      { severity: 'ok', text: 'Retention periods are stated and sensible.' },
    ],
    templateAvailable: true,
    history: [
      { date: '12 Mar 2026', event: 'Uploaded a refreshed version after adding the new rota app', by: 'you' },
      { date: '12 Mar 2026', event: 'Assessed: no issues found', by: 'reeve' },
    ],
  },
  {
    id: 'right-to-work',
    name: 'Right to work check records',
    category: 'legally-required',
    status: 'ok',
    summary:
      'Evidence you checked every employee’s right to work before they started. Fines for getting this wrong reach £60,000 per person.',
    lastChecked: 'Checked today',
    findings: [
      { severity: 'ok', text: 'Checks recorded for all 6 current employees, with dated copies.' },
      { severity: 'ok', text: 'Follow-up date diarised for the one time-limited visa on file (Amara Diallo, due 12 Sep).' },
    ],
    templateAvailable: false,
    history: [
      { date: '14 May 2026', event: 'Added check for Amara Diallo, with the follow-up diarised for 12 Sep', by: 'you' },
      { date: '1 May 2026', event: 'Added check for Sofia Reyes', by: 'you' },
    ],
  },
  {
    id: 'pay-statements',
    name: 'Payslips & wage records',
    category: 'legally-required',
    status: 'ok',
    summary:
      'Itemised payslips are a legal right, and you must keep pay records six years. Also your defence for minimum-wage and tips compliance.',
    lastChecked: 'Checked today',
    findings: [
      { severity: 'ok', text: 'Payslips itemise hours for your variable-hours staff, as required.' },
      { severity: 'ok', text: 'Tips policy reflects the Employment (Allocation of Tips) Act 2023.' },
    ],
    templateAvailable: false,
    history: [{ date: '3 Feb 2026', event: 'Payroll records confirmed complete for 2025–26', by: 'you' }],
  },
  {
    id: 'harassment-policy',
    name: 'Sexual harassment prevention policy',
    category: 'legally-required',
    status: 'missing',
    summary:
      'Since October 2024 employers have a proactive legal duty to take reasonable steps to prevent sexual harassment. A policy plus staff briefing is the baseline evidence that you did.',
    findings: [
      {
        severity: 'risk',
        text: 'No policy in place. If a complaint arose today you would struggle to show you took the "reasonable steps" the Worker Protection Act requires, and tribunal awards can be uplifted by 25%.',
        fix: 'Start from reeve’s template (it covers customer-facing risks), then brief the team and log the date.',
      },
      {
        severity: 'warn',
        text: 'Customer-facing hospitality teams are higher-risk: the duty covers harassment by customers, not just colleagues.',
      },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['harassment-duty'],
    history: [{ date: '6 Apr 2026', event: 'Flagged as missing: legally required for your team', by: 'reeve' }],
  },
  {
    id: 'disciplinary-policy',
    name: 'Disciplinary & dismissal procedure',
    category: 'essential',
    status: 'needs-update',
    summary:
      'How you handle misconduct and poor performance. Tribunals check you followed the ACAS Code, and this document is your evidence.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'disciplinary-procedure.docx',
    findings: [
      {
        severity: 'warn',
        text: 'Your absence trigger points still reference the three "waiting days" for statutory sick pay. Those were abolished in April 2026: sick pay now starts on day one.',
        fix: 'Delete the waiting-days sentence and count absence occasions from day one. Reeve has drafted the replacement wording.',
      },
      { severity: 'ok', text: 'Investigation, hearing and appeal stages all follow the ACAS Code of Practice.' },
      { severity: 'ok', text: 'Right to be accompanied is stated correctly.' },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['ssp-day-one'],
    history: [
      { date: '6 Apr 2026', event: 'Flagged after the April SSP change: one clause to update', by: 'reeve' },
      { date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' },
    ],
    excerpts: [
      { text: '2.1 Minor lapses will normally be dealt with informally by your line manager. Formal action follows the stages set out below.' },
      {
        text: '5.3 Absence patterns are reviewed once statutory sick pay becomes payable after the three waiting days, or where three or more occasions occur in a rolling three-month period.',
        issue: 'References the abolished SSP waiting days. Since April 2026 sick pay starts on day one.',
        fix: 'Reword to: "Absence patterns are reviewed where three or more occasions occur in a rolling three-month period."',
      },
      { text: '7.1 You have the right to be accompanied at any formal hearing by a colleague or a trade union representative.' },
    ],
  },
  {
    id: 'grievance-procedure',
    name: 'Grievance procedure',
    category: 'essential',
    status: 'ok',
    summary:
      'The route an employee follows to raise a problem formally. Following it properly protects you if things escalate.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'grievance-procedure.docx',
    findings: [
      { severity: 'ok', text: 'Follows the ACAS Code: written grievance, meeting, decision, appeal.' },
      { severity: 'ok', text: 'Names a second decision-maker for appeals, which is good practice in a small team.' },
    ],
    templateAvailable: true,
    history: [
      { date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' },
      { date: '7 Jul 2026', event: 'Referenced when the kitchen grievance case opened', by: 'reeve' },
    ],
  },
  {
    id: 'sickness-policy',
    name: 'Sickness absence & sick pay policy',
    category: 'essential',
    status: 'needs-update',
    summary: 'What staff do when they’re ill, what you pay, and how you manage long-term absence.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'sickness-policy.docx',
    findings: [
      {
        severity: 'risk',
        text: 'States that statutory sick pay starts on the fourth day of absence. Since 6 April 2026 SSP is payable from day one, with no lower earnings limit. Paying to the old rules is an unlawful deduction.',
        fix: 'Replace clause 3.2 with the day-one wording. Reeve has drafted it below in the document view.',
      },
      {
        severity: 'warn',
        text: 'The lower earnings limit reference should be removed: every employee now qualifies.',
        fix: 'Delete the second sentence of clause 3.3.',
      },
      { severity: 'ok', text: 'Fit note and return-to-work process are clear and reasonable.' },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['ssp-day-one'],
    history: [
      { date: '6 Apr 2026', event: 'Flagged after the April SSP change: two clauses to update', by: 'reeve' },
      { date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' },
      { date: '9 Jul 2026', event: 'Referenced when Marcus Webb’s absence case opened', by: 'reeve' },
    ],
    excerpts: [
      { text: '3.1 Tell your manager you are unwell before 9am on the first day of absence, and each day after that unless a fit note covers the period.' },
      {
        text: '3.2 Statutory sick pay is payable from the fourth qualifying day of absence, in line with the statutory waiting period.',
        issue: 'Wrong since 6 April 2026: SSP is now payable from day one. Paying to this clause is an unlawful deduction.',
        fix: 'Replace with: "Statutory sick pay is payable from the first qualifying day of absence."',
      },
      {
        text: '3.3 To qualify, your average weekly earnings must be at or above the lower earnings limit set by HMRC.',
        issue: 'The lower earnings limit no longer applies: every employee qualifies.',
        fix: 'Delete this sentence.',
      },
      { text: '4.1 A fit note is required for any absence longer than seven calendar days, and a return-to-work conversation follows every absence.' },
    ],
  },
  {
    id: 'working-time',
    name: 'Working time & holiday policy',
    category: 'essential',
    status: 'ok',
    summary:
      'Hours, rest breaks, and the 5.6 weeks’ statutory holiday, including how you handle accrual for your part-time weekend staff.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'working-time-policy.docx',
    findings: [
      { severity: 'ok', text: 'Holiday accrual for irregular-hours staff uses the 12.07% method correctly.' },
      { severity: 'ok', text: 'Rest break rules meet the Working Time Regulations for shifts over six hours.' },
    ],
    templateAvailable: true,
    history: [{ date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'staff-handbook',
    name: 'Staff handbook',
    category: 'best-practice',
    status: 'ok',
    summary:
      'One place your team can find the rules: conduct, dress code, phones, discounts. Not legally required, but it makes everything else easier to enforce.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 8 Jun 2026',
    fileName: 'bloom-handbook-v2.pdf',
    findings: [
      { severity: 'ok', text: 'Consistent with your contracts and policies: no contradictions found.' },
      { severity: 'ok', text: 'Marked non-contractual, so you can update it without consent from each employee.' },
    ],
    templateAvailable: true,
    history: [
      { date: '8 Jun 2026', event: 'Uploaded v2, adding the discounts and phones sections', by: 'you' },
      { date: '14 Oct 2025', event: 'Uploaded v1', by: 'you' },
    ],
  },
  {
    id: 'flexible-working',
    name: 'Flexible working policy',
    category: 'best-practice',
    status: 'ok',
    summary:
      'Every employee can request flexible working from day one, and you must handle requests within two months. A policy keeps you on the rails.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'flexible-working-policy.docx',
    findings: [
      { severity: 'ok', text: 'Reflects the day-one right and the two-month decision window.' },
      { severity: 'ok', text: 'Lists the eight lawful refusal grounds, used correctly in Priya’s request in May.' },
    ],
    templateAvailable: true,
    history: [
      { date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' },
      { date: '2 Jun 2026', event: 'Referenced when Priya’s request was agreed', by: 'reeve' },
    ],
  },
  {
    id: 'family-leave',
    name: 'Family leave policy',
    category: 'best-practice',
    status: 'needs-update',
    summary:
      'Maternity, paternity, adoption and parental leave in one place, so nobody has to work it out from GOV.UK mid-crisis.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'family-leave-policy.docx',
    findings: [
      {
        severity: 'warn',
        text: 'Still says paternity leave needs 26 weeks’ service. Since April 2026 paternity and ordinary parental leave are day-one rights.',
        fix: 'One sentence to change. Reeve has drafted it in the document view.',
      },
      { severity: 'ok', text: 'Maternity provisions are correct and clearly written.' },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['family-leave-day-one'],
    history: [
      { date: '6 Apr 2026', event: 'Flagged after the April family leave change', by: 'reeve' },
      { date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' },
    ],
    excerpts: [
      { text: '2.1 Maternity leave: up to 52 weeks, with statutory maternity pay for eligible employees for up to 39 weeks.' },
      {
        text: '3.1 Paternity leave is available to employees with at least 26 weeks’ continuous service by the qualifying week.',
        issue: 'Out of date: since April 2026, paternity leave is a day-one right with no qualifying service.',
        fix: 'Replace with: "Paternity leave is available from your first day of employment."',
      },
    ],
  },
  {
    id: 'equal-opportunities',
    name: 'Equal opportunities policy',
    category: 'best-practice',
    status: 'ok',
    summary:
      'Your commitment to hiring and managing without discrimination. First thing a tribunal asks for in any discrimination claim.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'equal-opportunities.docx',
    findings: [{ severity: 'ok', text: 'Covers all nine protected characteristics under the Equality Act 2010.' }],
    templateAvailable: true,
    history: [{ date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'probation-policy',
    name: 'Probation & induction policy',
    category: 'best-practice',
    status: 'ok',
    summary:
      'How new starters are reviewed and confirmed. About to matter much more: unfair dismissal protection moves to six months in January 2027.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'probation-policy.docx',
    findings: [
      { severity: 'ok', text: 'Review points at weeks 4, 8 and 12 with written outcomes: exactly the evidence trail you’ll want under the 2027 rules.' },
      {
        severity: 'warn',
        text: 'From January 2027, plan to run the full dismissal procedure for anyone past six months: casual probation exits stop being safe.',
      },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['unfair-dismissal-6mo'],
    history: [{ date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'it-social-media',
    name: 'IT & social media policy',
    category: 'optional',
    status: 'ok',
    summary:
      'What staff can do on work systems and what they can say about the business online. Matters more than you’d think when things go wrong.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 3 Feb 2026',
    fileName: 'it-social-policy.docx',
    findings: [{ severity: 'ok', text: 'Monitoring statement is consistent with your staff privacy notice.' }],
    templateAvailable: true,
    history: [{ date: '3 Feb 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'driving-for-work',
    name: 'Driving for work policy',
    category: 'not-relevant',
    status: 'ok',
    summary:
      'Needed when staff drive as part of the job, like deliveries, site visits or company vehicles. Nobody at Bloom & Forage drives for work, so this one is safely skippable.',
    findings: [
      {
        severity: 'ok',
        text: 'Not needed for your business: no deliveries, no company vehicles, no staff driving on work time. If that changes, say you start a delivery round, reeve will move this onto your requirements.',
      },
    ],
    templateAvailable: true,
    history: [{ date: '4 Feb 2026', event: 'Marked not relevant, based on your company profile', by: 'reeve' }],
  },
  {
    id: 'noncompete-addendum',
    name: 'Non-compete addendum (12 months)',
    category: 'should-not-have',
    status: 'needs-update',
    summary:
      'A 12-month non-compete for café staff won’t survive a court and misleads everyone who signs it. Restraints must protect a legitimate business interest and go no further.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 5 Mar 2026',
    fileName: 'noncompete-addendum.docx',
    findings: [
      {
        severity: 'risk',
        text: 'A 12-month, all-of-Bristol non-compete for front-of-house staff is almost certainly unenforceable: courts strike down restraints wider than needed to protect the business. Keeping it risks unfair-terms complaints and gives false comfort.',
        fix: 'Withdraw the addendum. If you’re protecting supplier terms and recipes, a confidentiality clause plus a 3-month non-solicitation of staff is enforceable and does the real work.',
      },
    ],
    templateAvailable: true,
    history: [
      { date: '5 Mar 2026', event: 'Uploaded', by: 'you' },
      { date: '5 Mar 2026', event: 'Flagged: unenforceable as drafted, with a replacement suggested', by: 'reeve' },
    ],
    excerpts: [
      {
        text: '1.1 For twelve months after employment ends, the employee shall not work for, own or assist any food or drink business within the city of Bristol.',
        issue: 'Far wider than any legitimate business interest for this role: a court would almost certainly refuse to enforce it.',
        fix: 'Replace with a confidentiality clause and a 3-month non-solicitation of colleagues. reeve’s template has both.',
      },
    ],
  },
];

const FERNWELL_DOCUMENTS: CompanyDocument[] = [
  {
    id: 'employment-contract',
    name: 'Employment contract',
    category: 'legally-required',
    status: 'ok',
    summary: 'Written statement of particulars for each of your three employees.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 20 Jan 2026',
    fileName: 'fernwell-contract-v2.docx',
    findings: [{ severity: 'ok', text: 'All required particulars present; notice periods compliant.' }],
    templateAvailable: true,
    linkedLawChangeIds: ['unfair-dismissal-6mo'],
    history: [{ date: '20 Jan 2026', event: 'Uploaded v2', by: 'you' }],
  },
  {
    id: 'privacy-notice',
    name: 'Staff privacy notice (UK GDPR)',
    category: 'legally-required',
    status: 'needs-update',
    summary: 'Tells your team what personal data you hold and why.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 11 Sep 2024',
    fileName: 'privacy-notice-2024.pdf',
    findings: [
      {
        severity: 'warn',
        text: 'Doesn’t mention the client-project time tracking you introduced in March, which processes employee activity data.',
        fix: 'Add a paragraph covering the time-tracking data. Reeve has drafted it.',
      },
    ],
    templateAvailable: true,
    history: [
      { date: '15 Mar 2026', event: 'Flagged after time-tracking was added to your profile', by: 'reeve' },
      { date: '11 Sep 2024', event: 'Uploaded', by: 'you' },
    ],
  },
  {
    id: 'harassment-policy',
    name: 'Sexual harassment prevention policy',
    category: 'legally-required',
    status: 'ok',
    summary: 'Your evidence of "reasonable steps" under the Worker Protection Act duty.',
    lastChecked: 'Checked today',
    uploaded: 'Uploaded 2 Nov 2024',
    fileName: 'fernwell-harassment-policy.docx',
    findings: [{ severity: 'ok', text: 'Policy in place and briefed to the team, with a note of the briefing date.' }],
    templateAvailable: true,
    linkedLawChangeIds: ['harassment-duty'],
    history: [{ date: '2 Nov 2024', event: 'Uploaded and briefed to the team', by: 'you' }],
  },
  {
    id: 'grievance-procedure',
    name: 'Grievance procedure',
    category: 'essential',
    status: 'ok',
    summary: 'The formal route for raising problems.',
    lastChecked: 'Checked today',
    findings: [{ severity: 'ok', text: 'Follows the ACAS Code.' }],
    templateAvailable: true,
    history: [{ date: '20 Jan 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'disciplinary-policy',
    name: 'Disciplinary & dismissal procedure',
    category: 'essential',
    status: 'ok',
    summary: 'How misconduct and performance issues are handled.',
    lastChecked: 'Checked today',
    findings: [{ severity: 'ok', text: 'ACAS-compliant, updated for day-one sick pay in April.' }],
    templateAvailable: true,
    linkedLawChangeIds: ['ssp-day-one'],
    history: [{ date: '10 Apr 2026', event: 'Uploaded update for day-one sick pay', by: 'you' }],
  },
  {
    id: 'working-time',
    name: 'Working time & holiday policy',
    category: 'essential',
    status: 'ok',
    summary: 'Hours, breaks and statutory holiday.',
    lastChecked: 'Checked today',
    findings: [{ severity: 'ok', text: 'Compliant, including the hybrid-working hours addendum.' }],
    templateAvailable: true,
    history: [{ date: '20 Jan 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'staff-handbook',
    name: 'Staff handbook',
    category: 'best-practice',
    status: 'ok',
    summary: 'House rules in one place.',
    lastChecked: 'Checked today',
    findings: [{ severity: 'ok', text: 'Consistent with contracts and policies.' }],
    templateAvailable: true,
    history: [{ date: '20 Jan 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'it-social-media',
    name: 'IT & social media policy',
    category: 'optional',
    status: 'ok',
    summary: 'Client confidentiality makes this one earn its keep in an agency.',
    lastChecked: 'Checked today',
    findings: [{ severity: 'ok', text: 'Covers client data and portfolio-use permissions.' }],
    templateAvailable: true,
    history: [{ date: '20 Jan 2026', event: 'Uploaded current version', by: 'you' }],
  },
];

const HARTLEY_DOCUMENTS: CompanyDocument[] = [
  {
    id: 'employment-contract',
    name: 'Employment contract',
    category: 'legally-required',
    status: 'needs-update',
    summary: 'Written particulars for all eleven employees, including your casual baristas.',
    lastChecked: 'Checked yesterday',
    uploaded: 'Uploaded 30 Jun 2026',
    fileName: 'hartley-contracts.zip',
    findings: [
      {
        severity: 'risk',
        text: 'Your four casual staff are on zero-hours terms with no guaranteed-hours review. The 2027 rules will give them a right to a contract reflecting their regular hours, so start tracking those hours now.',
        fix: 'Start a simple hours log for casual staff now; reeve will draft the guaranteed-hours review clause when the rules land.',
      },
      { severity: 'warn', text: 'Two contracts are missing the pension auto-enrolment particulars.', fix: 'Add the pension particulars, a one-paragraph insert reeve has drafted.' },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['zero-hours', 'unfair-dismissal-6mo'],
    history: [{ date: '30 Jun 2026', event: 'Uploaded all current contracts', by: 'you' }],
  },
  {
    id: 'h-and-s-policy',
    name: 'Health & safety policy',
    category: 'legally-required',
    status: 'ok',
    summary: 'Required in writing at your headcount; roastery machinery raises the stakes.',
    lastChecked: 'Checked yesterday',
    findings: [{ severity: 'ok', text: 'Covers roastery equipment, hot works and delivery driving.' }],
    templateAvailable: true,
    history: [{ date: '30 Jun 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'harassment-policy',
    name: 'Sexual harassment prevention policy',
    category: 'legally-required',
    status: 'missing',
    summary: 'The proactive prevention duty applies to both cafés and the roastery.',
    findings: [
      { severity: 'risk', text: 'No policy in place across any site. This is the first gap to close.', fix: 'Start from reeve’s template and brief each site manager.' },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['harassment-duty'],
    history: [{ date: '1 Jul 2026', event: 'Flagged as missing: legally required for your team', by: 'reeve' }],
  },
  {
    id: 'right-to-work',
    name: 'Right to work check records',
    category: 'legally-required',
    status: 'needs-update',
    summary: 'Evidence of pre-employment checks for all staff.',
    lastChecked: 'Checked yesterday',
    findings: [
      { severity: 'warn', text: 'Checks missing for two seasonal hires who started in May.', fix: 'Run the checks now and record the date. Late is far better than never.' },
    ],
    templateAvailable: false,
    history: [{ date: '1 Jul 2026', event: 'Flagged two missing checks', by: 'reeve' }],
  },
  {
    id: 'privacy-notice',
    name: 'Staff privacy notice (UK GDPR)',
    category: 'legally-required',
    status: 'ok',
    summary: 'What personal data you hold on staff and why.',
    lastChecked: 'Checked yesterday',
    findings: [{ severity: 'ok', text: 'In place and current.' }],
    templateAvailable: true,
    history: [{ date: '30 Jun 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'sickness-policy',
    name: 'Sickness absence & sick pay policy',
    category: 'essential',
    status: 'missing',
    summary: 'What staff do when ill and what you pay, now from day one.',
    findings: [
      { severity: 'risk', text: 'No written policy. With 11 staff across three sites, absence is being handled differently by each manager.', fix: 'Start from reeve’s template, which already reflects the April day-one rules.' },
    ],
    templateAvailable: true,
    linkedLawChangeIds: ['ssp-day-one'],
    history: [{ date: '1 Jul 2026', event: 'Flagged as missing', by: 'reeve' }],
  },
  {
    id: 'disciplinary-policy',
    name: 'Disciplinary & dismissal procedure',
    category: 'essential',
    status: 'needs-update',
    summary: 'Tribunal-proofing for misconduct and performance dismissals.',
    lastChecked: 'Checked yesterday',
    findings: [{ severity: 'warn', text: 'Pre-dates the ACAS Code refresh and the April sick pay changes.', fix: 'Update the absence triggers and re-issue. Reeve has the wording.' }],
    templateAvailable: true,
    linkedLawChangeIds: ['ssp-day-one'],
    history: [{ date: '30 Jun 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'grievance-procedure',
    name: 'Grievance procedure',
    category: 'essential',
    status: 'ok',
    summary: 'The formal route for raising problems.',
    lastChecked: 'Checked yesterday',
    findings: [{ severity: 'ok', text: 'Follows the ACAS Code.' }],
    templateAvailable: true,
    history: [{ date: '30 Jun 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'working-time',
    name: 'Working time & holiday policy',
    category: 'essential',
    status: 'ok',
    summary: 'Hours, breaks and holiday across shifts and sites.',
    lastChecked: 'Checked yesterday',
    findings: [{ severity: 'ok', text: 'Holiday accrual for casual staff uses the 12.07% method.' }],
    templateAvailable: true,
    history: [{ date: '30 Jun 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'staff-handbook',
    name: 'Staff handbook',
    category: 'best-practice',
    status: 'missing',
    summary: 'One rulebook across three sites would stop the drift between café managers.',
    findings: [
      { severity: 'warn', text: 'No handbook. Site-level "house rules" documents contradict each other on breaks and phone use.', fix: 'Start from the template and retire the site-level rules.' },
    ],
    templateAvailable: true,
    history: [{ date: '1 Jul 2026', event: 'Flagged as missing', by: 'reeve' }],
  },
  {
    id: 'flexible-working',
    name: 'Flexible working policy',
    category: 'best-practice',
    status: 'ok',
    summary: 'Day-one right to request; two-month decision window.',
    lastChecked: 'Checked yesterday',
    findings: [{ severity: 'ok', text: 'Reflects the current rules.' }],
    templateAvailable: true,
    history: [{ date: '30 Jun 2026', event: 'Uploaded current version', by: 'you' }],
  },
  {
    id: 'equal-opportunities',
    name: 'Equal opportunities policy',
    category: 'best-practice',
    status: 'ok',
    summary: 'Your Equality Act commitment in writing.',
    lastChecked: 'Checked yesterday',
    findings: [{ severity: 'ok', text: 'Covers all protected characteristics.' }],
    templateAvailable: true,
    history: [{ date: '30 Jun 2026', event: 'Uploaded current version', by: 'you' }],
  },
];

const DOCUMENTS_BY_COMPANY: Record<string, CompanyDocument[]> = {
  'bloom-forage': BLOOM_DOCUMENTS,
  'fernwell-studio': FERNWELL_DOCUMENTS,
  'hartley-roasters': HARTLEY_DOCUMENTS,
};

export function getDocuments(companyId: string): CompanyDocument[] {
  return DOCUMENTS_BY_COMPANY[companyId] ?? BLOOM_DOCUMENTS;
}

export function getDocument(companyId: string, docId: string): CompanyDocument | undefined {
  return getDocuments(companyId).find((d) => d.id === docId);
}

/**
 * Checklist arithmetic used across the app. "Not relevant" documents sit
 * outside the maths entirely; a "should not have" document that exists is
 * always an issue, whatever its status.
 */
export function docStats(docs: CompanyDocument[]) {
  const relevant = docs.filter((d) => d.category !== 'not-relevant');
  const isOk = (d: CompanyDocument) => d.status === 'ok' && d.category !== 'should-not-have';

  const byCategory = {} as Record<DocCategory, { ok: number; total: number }>;
  for (const cat of Object.keys(CATEGORY_META) as DocCategory[]) {
    const inCat = docs.filter((d) => d.category === cat);
    byCategory[cat] = { ok: inCat.filter(isOk).length, total: inCat.length };
  }

  const required = relevant.filter((d) => d.category === 'legally-required');
  const supporting = relevant.filter(
    (d) => d.category !== 'legally-required' && d.category !== 'should-not-have',
  );

  return {
    /** Documents that count (everything except "not relevant"). */
    total: relevant.length,
    totalOk: relevant.filter(isOk).length,
    requiredOk: required.filter(isOk).length,
    requiredTotal: required.length,
    supportingOk: supporting.filter(isOk).length,
    supportingTotal: supporting.length,
    byCategory,
    needsAttention: relevant.filter((d) => !isOk(d)).length,
    compliance:
      relevant.length === 0
        ? 0
        : Math.round((relevant.filter(isOk).length / relevant.length) * 100),
  };
}

/* ------------------------------------------------------------ Assertions */

const BLOOM_ASSERTIONS: CompanyAssertion[] = [
  {
    id: 'hse-poster',
    statement: 'The health and safety law poster is displayed where staff can read it.',
    why: 'Displaying the HSE poster (or giving each worker the equivalent leaflet) is a legal requirement.',
    category: 'legally-required',
    confirmed: true,
    confirmedOn: 'Confirmed 3 Feb 2026',
  },
  {
    id: 'eli-certificate',
    statement: 'The employer’s liability insurance certificate is displayed or easily accessible.',
    why: 'Cover of at least £5m is compulsory, and the certificate must be available to staff.',
    category: 'legally-required',
    confirmed: true,
    confirmedOn: 'Confirmed 3 Feb 2026',
  },
  {
    id: 'risk-assessment',
    statement: 'A workplace risk assessment has been done in the last 12 months.',
    why: 'With five or more employees the significant findings must be written down.',
    category: 'legally-required',
    confirmed: false,
    note: 'Last confirmed February 2025. Re-confirm after the kitchen refit.',
  },
  {
    id: 'first-aid',
    statement: 'There’s a stocked first-aid kit and a named first-aider on every shift.',
    why: 'First-aid provision has to match your risks, and kitchens set that bar higher.',
    category: 'essential',
    confirmed: true,
    confirmedOn: 'Confirmed 3 Feb 2026',
  },
  {
    id: 'harassment-briefing',
    statement: 'The team has been briefed on preventing sexual harassment.',
    why: 'The briefing is the practical half of the "reasonable steps" duty: the policy alone isn’t enough.',
    category: 'legally-required',
    confirmed: false,
    note: 'Do this once your prevention policy is in place. Reeve has a 15-minute briefing outline.',
  },
  {
    id: 'fire-walkthrough',
    statement: 'A fire evacuation walkthrough has been done with all current staff.',
    why: 'Everyone needs to know the routes and the assembly point, and new starters are the ones who don’t.',
    category: 'essential',
    confirmed: true,
    confirmedOn: 'Confirmed 12 May 2026',
  },
  {
    id: 'hygiene-ratings',
    statement: 'Food hygiene certificates are current and the rating is displayed.',
    why: 'Sector-specific, but customers and inspectors both look for it.',
    category: 'best-practice',
    confirmed: true,
    confirmedOn: 'Confirmed 3 Feb 2026',
  },
];

const FERNWELL_ASSERTIONS: CompanyAssertion[] = [
  {
    id: 'hse-poster',
    statement: 'The health and safety law poster is displayed, or each worker has the leaflet.',
    why: 'A legal requirement even in a studio of three.',
    category: 'legally-required',
    confirmed: true,
    confirmedOn: 'Confirmed 20 Jan 2026',
  },
  {
    id: 'eli-certificate',
    statement: 'The employer’s liability insurance certificate is displayed or easily accessible.',
    why: 'Compulsory cover; the certificate must be available to staff.',
    category: 'legally-required',
    confirmed: true,
    confirmedOn: 'Confirmed 20 Jan 2026',
  },
  {
    id: 'dse-assessment',
    statement: 'Display screen equipment assessments have been done for everyone.',
    why: 'Everyone at Fernwell works at a screen all day, so DSE assessments are the rule that actually applies here.',
    category: 'essential',
    confirmed: false,
    note: 'Quick self-assessment form per person. Reeve has one ready.',
  },
];

const HARTLEY_ASSERTIONS: CompanyAssertion[] = [
  {
    id: 'hse-poster',
    statement: 'The health and safety law poster is displayed at every site.',
    why: 'Required at each workplace: that means both cafés and the roastery.',
    category: 'legally-required',
    confirmed: false,
    note: 'Confirmed at the roastery only, so check both cafés.',
  },
  {
    id: 'eli-certificate',
    statement: 'The employer’s liability insurance certificate is displayed or easily accessible.',
    why: 'Compulsory cover; the certificate must be available to staff.',
    category: 'legally-required',
    confirmed: true,
    confirmedOn: 'Confirmed 30 Jun 2026',
  },
  {
    id: 'risk-assessment',
    statement: 'A written risk assessment covers the roastery machinery and hot works.',
    why: 'With 11 employees the significant findings must be written down, and machinery makes it urgent.',
    category: 'legally-required',
    confirmed: true,
    confirmedOn: 'Confirmed 30 Jun 2026',
  },
  {
    id: 'first-aid',
    statement: 'Each site has a stocked first-aid kit and a named first-aider.',
    why: 'Provision has to match the risks at every site, not just head office.',
    category: 'essential',
    confirmed: false,
    note: 'The Gloucester Road café has no named first-aider since May.',
  },
];

const ASSERTIONS_BY_COMPANY: Record<string, CompanyAssertion[]> = {
  'bloom-forage': BLOOM_ASSERTIONS,
  'fernwell-studio': FERNWELL_ASSERTIONS,
  'hartley-roasters': HARTLEY_ASSERTIONS,
};

export function getAssertions(companyId: string): CompanyAssertion[] {
  return ASSERTIONS_BY_COMPANY[companyId] ?? BLOOM_ASSERTIONS;
}

/* ------------------------------------------------------------- Templates */

export const TEMPLATES: DocTemplate[] = [
  {
    id: 'tpl-harassment',
    docId: 'harassment-policy',
    name: 'Sexual harassment prevention policy',
    description:
      'A policy meeting the Worker Protection Act duty: risk assessment prompts, reporting routes, and third-party (customer) harassment coverage.',
    fitNote: 'Tailored for customer-facing hospitality teams of 1–9 employees.',
  },
  {
    id: 'tpl-sickness',
    docId: 'sickness-policy',
    name: 'Sickness absence & sick pay policy',
    description:
      'Current-rules sickness policy: day-one statutory sick pay, no lower earnings limit, fit notes, return-to-work meetings and long-term absence.',
    fitNote: 'Reflects the April 2026 SSP changes out of the box.',
  },
  {
    id: 'tpl-handbook',
    docId: 'staff-handbook',
    name: 'Staff handbook',
    description:
      'A compact, non-contractual handbook covering conduct, communication, phones, discounts and how your policies fit together.',
    fitNote: 'Written for small teams, and it reads in fifteen minutes.',
  },
  {
    id: 'tpl-family-leave',
    docId: 'family-leave',
    name: 'Family leave policy',
    description:
      'Maternity, paternity, adoption, parental and carer’s leave with the April 2026 day-one rights already reflected.',
    fitNote: 'Includes the new day-one paternity right.',
  },
  {
    id: 'tpl-confidentiality',
    docId: 'noncompete-addendum',
    name: 'Confidentiality & non-solicitation clause',
    description:
      'The enforceable replacement for a broad non-compete: protects recipes, supplier terms and your team with a confidentiality clause plus a 3-month non-solicitation.',
    fitNote: 'Drafted to survive a court challenge, unlike a 12-month non-compete.',
  },
];

/* ------------------------------------------------------------- Law watch */

export const LAW_CHANGES: LawChange[] = [
  {
    id: 'harassment-duty',
    title: 'Duty to prevent sexual harassment',
    date: 'October 2024',
    sortDate: '2024-10',
    status: 'in-force',
    source: 'Worker Protection Act 2023',
    summary:
      'Employers must take reasonable steps to prevent sexual harassment of their staff, including by customers. Tribunals can uplift compensation by 25% where the duty was ignored.',
    whatToDo:
      'Put a prevention policy in place, brief the team, and keep a note of when you did. Reeve’s template covers the customer-facing risks specific to hospitality.',
    actionNeeded: true,
    affectedDocIds: ['harassment-policy'],
  },
  {
    id: 'ssp-day-one',
    title: 'Sick pay from day one',
    date: 'April 2026',
    sortDate: '2026-04',
    status: 'in-force',
    source: 'Employment Rights Act 2025',
    summary:
      'Statutory sick pay is now payable from the first day of sickness, for every employee, with the lower earnings limit removed. The old three "waiting days" are gone.',
    whatToDo:
      'Update your sickness policy and any absence triggers in your disciplinary procedure, and make sure payroll stopped applying waiting days in April.',
    actionNeeded: true,
    affectedDocIds: ['sickness-policy', 'disciplinary-policy'],
  },
  {
    id: 'family-leave-day-one',
    title: 'Day-one family leave',
    date: 'April 2026',
    sortDate: '2026-04',
    status: 'in-force',
    source: 'Employment Rights Act 2025',
    summary:
      'Paternity leave and ordinary parental leave are now day-one rights: the 26-week qualifying period has gone.',
    whatToDo:
      'Update your family leave policy so nobody is told they don’t qualify. One sentence to change; reeve has drafted it.',
    actionNeeded: true,
    affectedDocIds: ['family-leave'],
  },
  {
    id: 'unfair-dismissal-6mo',
    title: 'Unfair dismissal from six months',
    date: 'January 2027',
    sortDate: '2027-01',
    status: 'upcoming',
    source: 'Employment Rights Act 2025',
    summary:
      'Protection from unfair dismissal will start at six months’ service instead of two years. Casual probation exits after six months become tribunal risks.',
    whatToDo:
      'Your probation policy already builds the right evidence trail. Nearer the time we’ll flag the contract wording to change and the process to follow for anyone past six months.',
    actionNeeded: false,
    affectedDocIds: ['employment-contract', 'probation-policy'],
  },
  {
    id: 'fire-rehire',
    title: 'Fire and rehire restricted',
    date: 'January 2027',
    sortDate: '2027-01',
    status: 'upcoming',
    source: 'Employment Rights Act 2025',
    summary:
      'Dismissing staff to re-engage them on worse terms becomes automatically unfair in most circumstances.',
    whatToDo:
      'Nothing to change in your documents. If you ever need to vary contract terms, ask reeve first, because consultation will be the only safe route.',
    actionNeeded: false,
    affectedDocIds: [],
  },
  {
    id: 'zero-hours',
    title: 'Guaranteed hours for zero-hours workers',
    date: 'Late 2027 (expected)',
    sortDate: '2027-10',
    status: 'upcoming',
    source: 'Employment Rights Act 2025',
    summary:
      'Zero-hours and low-hours workers will get the right to a contract reflecting their regular hours, reasonable notice of shifts, and payment for cancelled ones.',
    whatToDo:
      'You have casual staff on the books. Start keeping their actual hours on record now: the reference period rules will lean on it.',
    actionNeeded: false,
    affectedDocIds: ['employment-contract'],
  },
];

export function getLawChange(id: string): LawChange | undefined {
  return LAW_CHANGES.find((l) => l.id === id);
}

/* --------------------------------------------------------------- Experts */

export const EXPERTS: Expert[] = [
  {
    id: 'eleanor-whitfield',
    name: 'Eleanor Whitfield',
    credentials: 'Chartered FCIPD · 18 years in HR practice',
    specialisms: ['Grievances', 'Disciplinaries', 'Workplace investigations'],
    responseTime: 'Replies within 4 working hours',
    initials: 'EW',
  },
  {
    id: 'daniel-osei',
    name: 'Daniel Osei',
    credentials: 'Chartered MCIPD · Employment relations specialist',
    specialisms: ['Redundancy', 'Restructuring', 'Settlement agreements'],
    responseTime: 'Replies within 1 working day',
    initials: 'DO',
  },
  {
    id: 'meera-krishnan',
    name: 'Meera Krishnan',
    credentials: 'Chartered MCIPD · Former ACAS conciliator',
    specialisms: ['Family leave', 'Flexible working', 'Contract changes'],
    responseTime: 'Replies within 1 working day',
    initials: 'MK',
  },
];

export function getExpert(id: string): Expert | undefined {
  return EXPERTS.find((e) => e.id === id);
}

/* ----------------------------------------------------------------- Cases */

const BLOOM_CASES: HrCase[] = [
  {
    id: 'resignation-jamie',
    title: 'Resignation: Jamie Patterson',
    kind: 'Leaver & offboarding',
    person: 'Jamie Patterson · Front of house',
    employeeId: 'emp-jamie',
    opened: 'Opened Mon 13 Jul',
    status: 'open',
    risk: 'low',
    summary:
      'Jamie resigned verbally on Monday. Their contract sets one month’s notice, making the last day 14 August. Reeve has laid out the offboarding steps.',
    nextStep: 'Get the resignation in writing and confirm the last working day of 14 August.',
    steps: [
      { title: 'Resignation received', detail: 'Verbal resignation to shift lead, Mon 13 Jul.', state: 'done' },
      {
        title: 'Confirm in writing',
        detail: 'Ask Jamie to confirm by email, then reply accepting and stating the last day: 14 August.',
        state: 'current',
      },
      { title: 'Calculate final pay & holiday', detail: '4.5 days’ accrued holiday to pay or take before leaving.', state: 'todo' },
      { title: 'Final pay & P45', detail: 'Process in the August payroll run; issue the P45 promptly.', state: 'todo' },
      { title: 'Offboarding checklist', detail: 'Keys, till codes, rota access, staff discount card.', state: 'todo' },
    ],
    relatedDocIds: ['employment-contract', 'working-time'],
  },
  {
    id: 'grievance-kitchen',
    title: 'Grievance: kitchen team',
    kind: 'Grievance',
    person: 'Raised by Tom Askew · Kitchen',
    employeeId: 'emp-tom',
    opened: 'Opened Tue 7 Jul',
    status: 'escalated',
    risk: 'high',
    summary:
      'Tom has raised a written grievance about shift allocation and comments made by a colleague. Because the complaint touches on potential discrimination, reeve recommended expert support, and Eleanor is now leading the process.',
    nextStep: 'Eleanor is preparing the investigation plan and will send it today for your sign-off.',
    steps: [
      { title: 'Grievance received in writing', detail: 'Tue 7 Jul, logged and acknowledged within 24 hours.', state: 'done' },
      { title: 'Escalated to a CIPD expert', detail: 'Potential discrimination element: Eleanor Whitfield engaged.', state: 'done' },
      { title: 'Investigation plan', detail: 'Interview list and timeline, prepared by Eleanor.', state: 'current' },
      { title: 'Grievance meeting', detail: 'To be chaired with Eleanor supporting.', state: 'todo' },
      { title: 'Outcome & appeal window', state: 'todo' },
    ],
    expertId: 'eleanor-whitfield',
    relatedDocIds: ['grievance-procedure', 'equal-opportunities'],
  },
  {
    id: 'sickness-marcus',
    title: 'Sickness absence: Marcus Webb',
    kind: 'Absence',
    person: 'Marcus Webb · Baker',
    employeeId: 'emp-marcus',
    opened: 'Opened Thu 9 Jul',
    status: 'waiting',
    risk: 'medium',
    summary:
      'Marcus is off with a back injury, day 9 of absence. Fit note received to 24 July. Sick pay is running from day one under the April rules.',
    nextStep: 'Check in with Marcus early next week: supportive contact, no pressure to return.',
    steps: [
      { title: 'Absence reported', detail: 'Thu 9 Jul, first day of sickness. SSP from day one applied.', state: 'done' },
      { title: 'Fit note received', detail: 'Covers to Thu 24 Jul: "not fit for work".', state: 'done' },
      { title: 'Keep-in-touch call', detail: 'A short, supportive check-in. Reeve has a script if you want one.', state: 'current' },
      { title: 'Return-to-work meeting', detail: 'On the day he returns; consider lighter duties for the first week.', state: 'todo' },
    ],
    relatedDocIds: ['sickness-policy'],
  },
  {
    id: 'probation-sofia',
    title: 'Probation review: Sofia Reyes',
    kind: 'Probation',
    person: 'Sofia Reyes · Barista',
    employeeId: 'emp-sofia',
    opened: 'Opened Wed 1 Jul',
    status: 'open',
    risk: 'low',
    summary:
      'Sofia’s 12-week probation ends 28 July. Reviews at weeks 4 and 8 were positive; the final review is due.',
    nextStep: 'Book the final probation review before 28 July and confirm the outcome in writing.',
    steps: [
      { title: 'Week 4 review', detail: 'Positive: punctuality and customer feedback strong.', state: 'done' },
      { title: 'Week 8 review', detail: 'Positive: signed off on solo opening shifts.', state: 'done' },
      { title: 'Final review & confirmation', detail: 'Due by 28 Jul. Confirm in writing either way.', state: 'current' },
    ],
    relatedDocIds: ['probation-policy', 'employment-contract'],
  },
  {
    id: 'flexible-priya',
    title: 'Flexible working request: Priya Shah',
    kind: 'Flexible working',
    person: 'Priya Shah · Supervisor',
    employeeId: 'emp-priya',
    opened: 'Opened 12 May · Resolved 2 Jun',
    status: 'resolved',
    risk: 'low',
    summary:
      'Priya asked to move to a four-day compressed week for childcare. Agreed with a three-month trial period, confirmed in writing on 2 June.',
    steps: [
      { title: 'Request received in writing', detail: '12 May: statutory request, day-one right.', state: 'done' },
      { title: 'Meeting held', detail: '21 May: options discussed, compressed week preferred.', state: 'done' },
      { title: 'Decision within two months', detail: 'Agreed with a trial to 2 Sep; contract variation letter issued.', state: 'done' },
    ],
    relatedDocIds: ['flexible-working', 'employment-contract'],
  },
];

const FERNWELL_CASES: HrCase[] = [
  {
    id: 'starter-ivy',
    title: 'New starter: Ivy Chen',
    kind: 'Starter & onboarding',
    person: 'Ivy Chen · Junior designer',
    employeeId: 'emp-ivy',
    opened: 'Opened 2 Jun',
    status: 'open',
    risk: 'low',
    summary:
      'Ivy joined on 2 June. Contract, right to work and payroll are all done. Her pension letter is the one thing still open.',
    nextStep: 'Issue Ivy’s pension auto-enrolment letter, due by 2 September.',
    steps: [
      { title: 'Contract signed and on file', detail: 'Signed before her first day.', state: 'done' },
      { title: 'Right to work check recorded', detail: 'Checked and dated 28 May.', state: 'done' },
      { title: 'Payroll set up', detail: 'HMRC starter checklist processed.', state: 'done' },
      { title: 'Pension auto-enrolment letter', detail: 'Due within six weeks of her start date (by 2 September).', state: 'current' },
      { title: 'Handbook and induction wrap-up', state: 'todo' },
    ],
    relatedDocIds: ['employment-contract'],
  },
];

const HARTLEY_CASES: HrCase[] = [
  {
    id: 'rtw-seasonal',
    title: 'Right to work: seasonal hires',
    kind: 'Compliance',
    person: 'Saskia Vane & Hugo Marsh · Casual baristas',
    opened: 'Opened 1 Jul',
    status: 'open',
    risk: 'high',
    summary:
      'Two seasonal hires started in May without recorded right to work checks. Civil penalties reach £60,000 per person, so this is the first thing to fix.',
    nextStep: 'Run both checks today and record the date. Late is far better than never.',
    steps: [
      { title: 'Gap found', detail: 'Flagged during the 1 July document check.', state: 'done' },
      { title: 'Run both checks', detail: 'Original documents or the Home Office online service.', state: 'current' },
      { title: 'Record and diarise', detail: 'Keep dated copies; diarise any follow-up for time-limited permissions.', state: 'todo' },
    ],
    relatedDocIds: ['right-to-work'],
  },
  {
    id: 'lateness-dan',
    title: 'Persistent lateness: Dan Kowalski',
    kind: 'Conduct',
    person: 'Dan Kowalski · Barista',
    employeeId: 'emp-dan',
    opened: 'Opened 24 Jun',
    status: 'waiting',
    risk: 'medium',
    summary:
      'Dan was late for five opening shifts in June. An informal chat happened on 26 June; a monitoring period is the right next stage before anything formal.',
    nextStep: 'Review the rota sign-ins on 24 July. If lateness continues, move to the formal stage of your disciplinary procedure.',
    steps: [
      { title: 'Pattern spotted', detail: 'Five late openings across June, logged from the rota.', state: 'done' },
      { title: 'Informal chat', detail: '26 June: expectations restated, notes kept.', state: 'done' },
      { title: 'Monitoring period', detail: 'Four weeks to 24 July.', state: 'current' },
      { title: 'Formal stage, if needed', detail: 'Only if the pattern continues. Reeve will lay out the steps.', state: 'todo' },
    ],
    relatedDocIds: ['disciplinary-policy'],
  },
];

const CASES_BY_COMPANY: Record<string, HrCase[]> = {
  'bloom-forage': BLOOM_CASES,
  'fernwell-studio': FERNWELL_CASES,
  'hartley-roasters': HARTLEY_CASES,
};

export function getCases(companyId: string): HrCase[] {
  return CASES_BY_COMPANY[companyId] ?? BLOOM_CASES;
}

export function getCase(companyId: string, caseId: string): HrCase | undefined {
  return getCases(companyId).find((c) => c.id === caseId);
}

export function getCasesForEmployee(companyId: string, employeeId: string): HrCase[] {
  return getCases(companyId).filter((c) => c.employeeId === employeeId);
}

/* ------------------------------------------------------------- Assistant */

export const SUGGESTED_PROMPTS = [
  'Someone’s just resigned. What do I need to do?',
  'Can I put a 12-month non-compete in a contract?',
  'An employee keeps being off sick on Mondays. What can I do?',
  'Do I have to give a leaver a reference?',
  'How do I run a disciplinary meeting properly?',
  'What has to be in a written statement of particulars?',
];

export const CONVERSATIONS: Conversation[] = [
  {
    id: 'conv-resignation',
    title: 'Resignation: what to do',
    startedAt: 'Today, 09:12',
    expiresIn: 'Expires in 30 days',
    messages: [
      {
        id: 'm1',
        role: 'user',
        text: 'Someone’s just resigned. What do I need to do?',
      },
      {
        id: 'm2',
        role: 'assistant',
        text: 'Get their resignation in writing and confirm their last day. Their contract puts them on one month’s notice, so their last day is 14 August. Then: calculate accrued holiday (they’re owed 4.5 days), arrange final pay and the P45 in the August payroll run, and work through the offboarding checklist: keys, till codes and rota access. Would you like me to open an offboarding case to track it?',
        grounding: ['Your Employment contract', 'ACAS guidance on resignations'],
      },
      { id: 'm3', role: 'user', text: 'Yes, open the case please.' },
      {
        id: 'm4',
        role: 'assistant',
        text: 'Done. I’ve opened "Resignation: Jamie Patterson" in your cases with the five offboarding steps laid out. The next step is confirming the last working day in writing; I’ve drafted an email you can send.',
        grounding: ['Your Employment contract'],
      },
    ],
  },
  {
    id: 'conv-noncompete',
    title: 'Non-compete clause length',
    startedAt: 'Tuesday, 16:40',
    expiresIn: 'Expires in 27 days',
    messages: [
      { id: 'm1', role: 'user', text: 'Can I put a 12-month non-compete on this contract?' },
      {
        id: 'm2',
        role: 'assistant',
        text: 'In the UK a court will only enforce what’s reasonable to protect the business, and 12 months is likely too long for this role. For a supervisor-level position, three to six months is the defensible range, and a well-drafted non-solicitation clause (customers and staff) usually protects you better than a broad non-compete. Here’s a safer clause to use instead.',
        grounding: ['UK restraint of trade case law', 'Your Employment contract template'],
      },
    ],
  },
  {
    id: 'conv-holiday',
    title: 'Holiday carry-over rules',
    startedAt: 'Mon 6 Jul, 08:55',
    expiresIn: 'Saved · won’t expire',
    saved: true,
    messages: [
      { id: 'm1', role: 'user', text: 'Can staff carry unused holiday into next year?' },
      {
        id: 'm2',
        role: 'assistant',
        text: 'The four weeks of EU-derived leave generally must be taken in-year; the extra 1.6 weeks can be carried over if you agree to it. Your working time policy currently allows up to 5 days’ carry-over with manager approval, which is compliant and sensible for a team your size. Staff off sick or on family leave keep their right to carry leave over.',
        grounding: ['Your Working time & holiday policy', 'Working Time Regulations 1998'],
      },
    ],
  },
];

/**
 * Canned assistant replies for the demo: first keyword match wins.
 * The fallback is used for anything unscripted.
 */
export const SCRIPTED_REPLIES: { keywords: string[]; text: string; grounding: string[] }[] = [
  {
    keywords: ['resign'],
    text: 'Get the resignation in writing and confirm the last day. The contract on file sets one month’s notice. From there it’s holiday pay, final pay and P45, and the offboarding checklist. I can open a case to track those steps.',
    grounding: ['Your Employment contract', 'ACAS guidance on resignations'],
  },
  {
    keywords: ['non-compete', 'noncompete', 'restrictive covenant'],
    text: 'Courts only enforce restraint clauses that are no wider than needed to protect the business. For most small-business roles a 3–6 month non-solicitation clause is safer and more enforceable than a long non-compete. I can draft one against your contract template. (You have a 12-month non-compete addendum in your requirements flagged for exactly this, and it’s worth replacing.)',
    grounding: ['UK restraint of trade case law', 'Your Non-compete addendum'],
  },
  {
    keywords: ['sick', 'absence', 'ill'],
    text: 'Since April 2026, statutory sick pay runs from day one with no lower earnings limit. For patterns of short absences, hold a return-to-work chat each time and keep it factual. Your sickness policy sets trigger points, though it still needs its April update (it’s in your requirements).',
    grounding: ['Your Sickness absence policy', 'Employment Rights Act 2025'],
  },
  {
    keywords: ['reference'],
    text: 'You generally don’t have to give a reference at all (finance is the exception). If you do, it must be true, accurate and fair. Many small employers give a short factual reference: role and dates only. That’s a safe default I can draft for you.',
    grounding: ['ACAS guidance on references'],
  },
  {
    keywords: ['disciplinary', 'misconduct'],
    text: 'Follow your disciplinary procedure and the ACAS Code: investigate first, invite them in writing with the allegations and possible outcomes, hold the hearing with a note-taker, allow a companion, decide, and offer an appeal. Skipping steps is what loses tribunals, not the decision itself. Want a step-by-step plan for your situation?',
    grounding: ['Your Disciplinary & dismissal procedure', 'ACAS Code of Practice'],
  },
  {
    keywords: ['particulars', 'written statement'],
    text: 'The written statement must include: names, start date, pay and interval, hours and days, holiday, probation terms, notice periods, job title, place of work, training requirements, and pensions (most on day one). Your current contract template covers all of it.',
    grounding: ['Employment Rights Act 1996, s.1', 'Your Employment contract'],
  },
];

export const FALLBACK_REPLY = {
  text: 'In the full product I’d answer this from current UK employment law, ACAS guidance and your own documents. This preview runs on sample data, so treat this as a placeholder answer, but this is where it would appear, grounded in your policies with sources shown below.',
  grounding: ['UK employment law knowledge base', 'Your documents'],
};

/* -------------------------------------------------------------- Activity */

// Human activity only: things people did. Reeve's background checks are shown
// on the documents themselves (lastChecked, history), never as activity.
export const ACTIVITY: ActivityItem[] = [
  {
    id: 'a1',
    icon: 'expert',
    text: 'Eleanor Whitfield replied on the kitchen team grievance: investigation plan ready for your sign-off.',
    when: 'Today, 08:47',
  },
  {
    id: 'a2',
    icon: 'chat',
    text: 'You asked about Jamie’s resignation and opened an offboarding case from the conversation.',
    when: 'Mon 13 Jul',
  },
  {
    id: 'a3',
    icon: 'doc',
    text: 'You uploaded Staff handbook v2.',
    when: '8 Jun',
  },
  {
    id: 'a4',
    icon: 'case',
    text: 'You resolved Priya Shah’s flexible working request: compressed week agreed with a trial to 2 Sep.',
    when: '2 Jun',
  },
  {
    id: 'a5',
    icon: 'expert',
    text: 'You added Amara Diallo to the team and recorded her right to work check.',
    when: '14 May',
  },
  {
    id: 'a6',
    icon: 'doc',
    text: 'You confirmed the fire evacuation walkthrough was done with all staff.',
    when: '12 May',
  },
];

/* ------------------------------------------------------------ Onboarding */

export const SECTORS = [
  'Hospitality & food service',
  'Retail',
  'Design & creative services',
  'Construction & trades',
  'Professional services',
  'Health & care',
  'Technology',
  'Food production & manufacturing',
  'Other',
];
